import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import dayjs from "dayjs";
import {
  ref,
  computed,
  watch,
  nextTick,
} from "vue";
import FullCalendar from "@fullcalendar/vue3";
import dayGridPlugin from "@fullcalendar/daygrid";
import type {
  CalendarOptions,
  ToolbarInput,
  EventInput,
} from "@fullcalendar/core";
import jaLocale from "@fullcalendar/core/locales/ja";

// ---- Props ----------------------------------------------

export default /*@__PURE__*/_defineComponent({
  __name: 'Calendar',
  props: {
    initialDate: { default: dayjs().format("YYYY-MM-DD") },
    showTitle: { type: Boolean, default: true },
    locale: { default: "ja" },
    columnWidth: { default: 50 },
    rowHeight: { default: 60 },
    iconSize: { default: 19 },
    dayIcons: {},
    underlinedDates: {},
    defaultIconUrl: { default: "/server/necs/images/calendar_circle.svg" }
  },
  emits: ["monthChange", "dateSelect"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

// ---- Constant ----------------------------------------------
// 選択中の日付
const selectedDate = ref<string>(dayjs().format("YYYY-MM-DD"));
// 表示中の月
const currentYearMonth = ref(props.initialDate);
// カレンダー本体
const calendarRef = ref<InstanceType<typeof FullCalendar> | null>(null);
const dynamicEvents = ref<EventInput[]>([]);

// 今月より先へは移動させない
function canMoveNextMonth(): boolean {
  return dayjs(currentYearMonth.value).isBefore(dayjs(), "month");
}

// ---- Event Notify ------------------------------------------
// イベント通定義義
const emit = __emit;

// ---- Functions ----------------------------------------------
// 体調アイコンを予定欄にセット
function normalizeDayIconsToEvents(
  src:
    | Array<{ date: string | Date; iconUrl: string }>
    | Record<string, string>
    | undefined
): EventInput[] {
  if (!src) return [];
  const list: EventInput[] = [];
  if (Array.isArray(src)) {
    for (const item of src) {
      const ymd = toYMD(item.date);
      if (!ymd || !item.iconUrl) continue;
      list.push({
        start: ymd,
        allDay: true,
        extendedProps: { iconUrl: item.iconUrl },
      });
    }
  } else {
    for (const [key, url] of Object.entries(src)) {
      const ymd = toYMD(key);
      if (!ymd || !url) continue;
      list.push({
        start: ymd,
        allDay: true,
        extendedProps: { iconUrl: url },
      });
    }
  }
  return list;
}

// YYYY-MM-DD書式変換
function toYMD(dateLike: string | Date): string | null {
  if (typeof dateLike === "string") {
    const isYmd = dayjs(dateLike, "YYYY-MM-DD", true).isValid();
    if (isYmd) return dateLike;
  }
  const d = dayjs(dateLike);
  if (!d.isValid()) return null;
  return d.format("YYYY-MM-DD");
}

// カレンダーオプション
const options = computed<CalendarOptions>(() => ({
  initialDate: dayjs(props.initialDate).toDate(),
  firstDay: 1, // 月曜始まり
  plugins: [dayGridPlugin],
  initialView: "dayGridMonth",
  headerToolbar: headerToolbar.value,
  locales: [jaLocale],
  locale: props.locale,
  // 全体高さは "auto" のまま（コンテナは縮めない）
  height: "auto",
  // 予定はアイコンだけを想定
  dayMaxEvents: 1,
  // レスポンシブ動作を完全に無効化
  handleWindowResize: false,

  // 指定のない日にはデフォルトアイコンをセル内に描画
  dayCellDidMount: (arg) => {
    const date = dayjs(arg.date).format("YYYY-MM-DD");

    // 今月以外のセルには挿入しない
    const inCurrentMonth = dayjs(arg.date).isSame(
      dayjs(arg.view.currentStart),
      "month"
    );
    if (!inCurrentMonth) return;
    const ymd = dayjs(arg.date).format("YYYY-MM-DD");
    // 親からアイコン指定がある日は何もしない（イベント描画に任せる）
    if (iconDateSet.value.has(ymd)) return;
    // デフォルトアイコンが未設定なら何もしない
    if (!props.defaultIconUrl) return;
    // 既存のセル構造に合わせて、本文領域（daygrid）へ挿入
    const container = arg.el.querySelector(".fc-daygrid-day-events") || arg.el;
    // 重複防止（再描画時の多重挿入を避ける）
    if (container.querySelector(".default-icon")) return;

    const img = document.createElement("img");
    img.src = props.defaultIconUrl!;
    img.width = props.iconSize;
    img.height = props.iconSize;
    img.alt = "";
    img.className = "default-icon";
    img.style.display = "block";
    img.style.margin = "4px auto 0";
    img.style.objectFit = "contain";
    container.appendChild(img);

    // クリックイベントをセット
    arg.el.addEventListener("click", () => {
      selectedDate.value = date;

      // 既存の選択を全て解除
      document
        .querySelectorAll(".fc-daygrid-day.is-selected")
        .forEach((el) => el.classList.remove("is-selected"));

      // このセルに付ける
      arg.el.classList.add("is-selected");

      emit("dateSelect", date);
    });
  },

  // 「日」を取って、数字のみを表示
  dayCellContent: (arg) => {
    const n = arg.date.getDate();
    return { html: String(n) };
  },

  dayCellClassNames: (arg) => {
    const ymd = dayjs(arg.date).format("YYYY-MM-DD");
    // return underlinedSet.value.has(ymd) ? ["is-underlined"] : [];
    const classes: string[] = [];

    if (underlinedSet.value.has(ymd)) {
      classes.push("is-underlined");
    }

    if (selectedDate.value === ymd) {
      classes.push("is-selected");
    }

    return classes;
  },

  // 親から渡されたアイコンを設定
  eventContent: (arg: any) => {
    const url = arg.event.extendedProps?.iconUrl as string | undefined;
    if (!url) return undefined;
    const img = document.createElement("img");
    img.src = url;
    img.width = props.iconSize;
    img.height = props.iconSize;
    img.alt = "";
    img.style.display = "block";
    img.style.pointerEvents = "none";
    return { domNodes: [img] };
  },

  eventDidMount: (arg: any) => {
    const cell = arg.el.closest(".fc-daygrid-day");
    if (!cell) return;
    const img = cell.querySelector("img.default-icon");
    if (img) img.remove();
  },

  // 月が切り替わったときのイベント
  datesSet: (arg: any) => {
    const nextMonth = dayjs(arg.view.currentStart).format("YYYY-MM");
    if (nextMonth === dayjs(currentYearMonth.value).format("YYYY-MM")) {
      // 月が替わっていないので無視
      return;
    }
    currentYearMonth.value = nextMonth;
    // イベントを一旦空にする（親vueから再設定）
    dynamicEvents.value = [];
    // 親vueへ今月を通知
    const currentStart = dayjs(arg.view.currentStart);
    emit("monthChange", {
      year: currentStart.year(),
      month: currentStart.month() + 1,
      yearMonth: currentStart.format("YYYY/MM"),
    });
  },

  events: eventInputs.value,

  titleFormat: { year: "numeric", month: "long" }, // 例: 2025年12月
  buttonText: {
    prev: "＜",
    next: "＞",
  },
  customButtons: {
    prevMonth: {
      text: "",
      click: () => {
        const api = calendarRef.value?.getApi();
        if (!api) return;
        api.prev();
      },
    },
    nextMonth: {
      text: "",
      click: () => {
        // 表示中が今月の場合は次月へ進ませない
        if (!canMoveNextMonth()) return;
        const api = calendarRef.value?.getApi();
        if (!api) return;
        api.next();
      },
    },
  },
}));

// デフォルトアイコンを再設定
function refreshDefaultIcons() {
  const cells = document.querySelectorAll(".fc-daygrid-day");

  cells.forEach((cell) => {
    const date = cell.getAttribute("data-date");
    if (!date) return;
    // 表示月の前後に出る日（FullCalendar の fc-day-other）は選択不可
    if (cell.classList.contains("fc-day-other")) return;

    const hasFeelingIcon = iconDateSet.value.has(date);
    const defaultIcon = cell.querySelector(".default-icon");

    // クリックイベントが付いていない場合は付ける
    if (!cell.dataset.clickBound) {
      cell.addEventListener("click", () => {
        selectedDate.value = date;

        document
          .querySelectorAll(".fc-daygrid-day.is-selected")
          .forEach((el) => el.classList.remove("is-selected"));

        cell.classList.add("is-selected");
        emit("dateSelect", date);
      });

      // 二重付与防止
      cell.dataset.clickBound = "true";
    }

    if (hasFeelingIcon) {
      if (defaultIcon) defaultIcon.remove();
    } else {
      if (!defaultIcon) {
        const img = document.createElement("img");
        img.src = props.defaultIconUrl!;
        img.className = "default-icon";
        img.width = props.iconSize;
        img.height = props.iconSize;
        img.style.pointerEvents = "none";
        cell.querySelector(".fc-daygrid-day-events")?.appendChild(img);
      }
    }
  });
}

// ---- Vue lifecycle -------------------------------------------
// 下線を引く日付をセット
const underlinedSet = computed<Set<string>>(() => {
  const list = props.underlinedDates ?? [];
  return new Set(
    list.map((d) =>
      dayjs(d).isValid() ? dayjs(d).format("YYYY-MM-DD") : String(d)
    )
  );
});
// タイトル（＜  YYYY年M月 ＞）
const headerToolbar = computed<false | ToolbarInput>(() =>
  props.showTitle
    ? { left: "prevMonth", center: "title", right: "nextMonth" }
    : false
);
// 体調アイコン表示
const eventInputs = computed<EventInput[]>(() =>
  normalizeDayIconsToEvents(props.dayIcons)
);
// 親からアイコン指定がある日（YYYY-MM-DD）の集合
const iconDateSet = computed<Set<string>>(() => {
  const set = new Set<string>();
  const src = props.dayIcons;
  if (!src) return set;
  if (Array.isArray(src)) {
    for (const item of src) {
      const ymd = toYMD(item.date);
      if (ymd && item.iconUrl) set.add(ymd);
    }
  } else {
    for (const key of Object.keys(src)) {
      const ymd = toYMD(key);
      if (ymd && src[key]) set.add(ymd);
    }
  }
  return set;
});
// 月が変わったタイミングでアイコンとイベントを再設定
watch(
  () => props.dayIcons,
  () => {
    nextTick(() => refreshDefaultIcons());
  }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(['cal-white', 'cal-fixed']),
    style: _normalizeStyle({
      '--col-width': `${_ctx.columnWidth}px`,
      '--row-height': `${_ctx.rowHeight}px`,
      '--icon-size': `${_ctx.iconSize}px`,
      '--calendar-prev-btn-icon':
        'url(/server/necs/images/icon_calendar_btn_prev.svg)',
      '--calendar-next-btn-icon': canMoveNextMonth()
        ? 'url(/server/necs/images/icon_calendar_btn_next.svg)'
        : 'url(/server/necs/images/icon_calendar_btn_next_DDD.svg)',
    })
  }, [
    _createVNode(_unref(FullCalendar), {
      ref_key: "calendarRef",
      ref: calendarRef,
      options: options.value
    }, null, 8, ["options"])
  ], 4))
}
}

})